import React from 'react';
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { TONE_BORDER, TONE_CHIP, TONE_TEXT } from '../../lib/rcu/catalog/feedback';
import { SEVERITY, issueMessage, severityOf } from '../../lib/rcu/catalog/issue';
import type { ConfigIssue, IssueTarget, Severity } from '../../lib/rcu/catalog/issue';
import { assertNever } from '../../lib/exhaustive';

export interface IssueListProps {
  issues: readonly ConfigIssue[];
  /** Called when a row points at a specific entity. */
  onSelectTarget?: (target: IssueTarget) => void;
}

const severityIcon = (severity: Severity): React.ReactElement => {
  switch (severity) {
    case SEVERITY.ERROR:
      return <CircleAlert className="h-4 w-4" />;
    case SEVERITY.WARNING:
      return <TriangleAlert className="h-4 w-4" />;
    case SEVERITY.INFO:
      return <Info className="h-4 w-4" />;
    default:
      return assertNever(severity);
  }
};

/** Validation output, worst first. Empty errors is the good news case. */
export const IssueList: React.FC<IssueListProps> = ({ issues, onSelectTarget }) => {
  if (issues.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-[#4CAF50]/10 px-4 py-3 text-sm text-[#2E7D32]">
        <CircleCheck className="h-4 w-4" />
        Configuration is consistent.
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {issues.map((issue) => {
        const severity = severityOf(issue);
        const target = issue.target;
        const clickable = onSelectTarget !== undefined && target.id !== undefined;

        return (
          <li
            key={`${issue.code}-${target.kind}-${target.id ?? ''}`}
            className={`flex items-start gap-3 rounded-r-xl border-l-4 bg-[#FAFAFA] px-4 py-3 ${TONE_BORDER[severity]}`}
          >
            <span className={`mt-0.5 ${TONE_TEXT[severity]}`}>{severityIcon(severity)}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-[#333333]">{issueMessage(issue)}</p>
              <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide ${TONE_CHIP[severity]}`}>
                {severity}
              </span>
            </div>
            {clickable && (
              <button
                type="button"
                onClick={() => onSelectTarget(target)}
                className="shrink-0 text-xs font-medium text-[#D4A054] hover:text-[#B8893F]"
              >
                Show
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default IssueList;
